import React from 'react';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import { LoadingProgress } from '../../../Components/ContentLoading';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Pagination } from '@material-ui/lab';
import RouteSkeleton from '../../../Components/RouteTransition/SkeletonLayouts';
import { useHistory, useLocation } from 'react-router-dom';
import { Dashboard } from '../../../Services/DashboardService';
import { ProjectServices } from '../../../Services/ProjectServices';
import { Paths } from '../../../Routes/routePaths';
import { routeForTarget } from '../../DashBoard/SuperadminInsights/attentionTargets';
import { useInsightsStyles } from '../../DashBoard/SuperadminInsights/styles';
import { formatCount, formatDate, requestErrorMessage } from '../../DashBoard/SuperadminInsights/utils';
import { Badge, DataTable, EmptyState, LearningHubBadge, Panel, UtilisationValue } from '../../DashBoard/SuperadminInsights/components/shared';
import commonStyles from '../../../theme/commonStyles';

const dashboardService = new Dashboard();
const projectService = new ProjectServices();
const PAGE_SIZES = [25, 50, 100];

const useProjectSearchStyles = makeStyles(theme => ({
  filterBar: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  searchField: {
    flex: '1 1 300px',
    maxWidth: 420,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: 'none',
    },
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    flex: '1 1 620px',
    marginLeft: 'auto',
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
      flexBasis: '100%',
      width: '100%',
    },
  },
  filterControl: {
    flex: '0 1 220px',
    minWidth: 180,
    [theme.breakpoints.down('xs')]: {
      flexBasis: '100%',
      width: '100%',
      maxWidth: 'none',
    },
  },
}));

const readInitial = (search, syncUrl) => {
  if (!syncUrl) return { search: '', customer_id: '', status: '', learning_hub: '', include_deleted: false, page: 1, page_size: 25 };
  const params = new URLSearchParams(search);
  const pageSize = Number(params.get('page_size'));
  return {
    search: params.get('search') || '',
    customer_id: params.get('customer_id') || '',
    status: params.get('status') || '',
    learning_hub: params.get('learning_hub') || '',
    include_deleted: params.get('include_deleted') === 'true',
    page: Math.max(1, Number(params.get('page')) || 1),
    page_size: PAGE_SIZES.includes(pageSize) ? pageSize : 25,
  };
};

const hubTiming = hub => {
  if (!hub) return '';
  if (hub.days_until_start !== null && hub.days_until_start !== undefined) return `Starts in ${formatCount(hub.days_until_start)} days`;
  if (hub.days_left !== null && hub.days_left !== undefined) return `${formatCount(hub.days_left)} days left`;
  return '';
};

const ProjectSearch = ({ syncUrl = false, embedded = false, showFullSearchLink = false, onInitialLoadingChange }) => {
  useInsightsStyles();
  const searchClasses = useProjectSearchStyles();
  const commonClasses = commonStyles();
  const history = useHistory();
  const location = useLocation();
  const [query, setQuery] = React.useState(() => readInitial(location.search, syncUrl));
  const [data, setData] = React.useState(null);
  const [customers, setCustomers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [paginationLoading, setPaginationLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const requestId = React.useRef(0);
  const urlSearch = React.useRef(location.search.replace(/^\?/, ''));
  const initialLoading = loading && !data;

  React.useEffect(() => {
    if (onInitialLoadingChange) onInitialLoadingChange(initialLoading);
  }, [initialLoading, onInitialLoadingChange]);

  React.useEffect(() => {
    let active = true;
    projectService.getCustomerList().then(response => {
      if (active) setCustomers(Array.isArray(response?.data) ? response.data : []);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    if (!syncUrl) return;
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== '' && value !== false && value !== null && value !== undefined && !(key === 'page' && value === 1) && !(key === 'page_size' && value === 25)) {
        params.set(key, String(value));
      }
    });
    const next = params.toString();
    if (next !== urlSearch.current) {
      urlSearch.current = next;
      history.replace({ pathname: location.pathname, search: next ? `?${next}` : '' });
    }
  }, [query, syncUrl, history, location.pathname]);

  React.useEffect(() => {
    const currentRequest = ++requestId.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const params = { ...query };
        if (!params.search) delete params.search;
        if (!params.customer_id) delete params.customer_id;
        if (!params.status) delete params.status;
        if (!params.learning_hub) delete params.learning_hub;
        if (!params.include_deleted) delete params.include_deleted;
        const response = await dashboardService.getSuperadminProjects(params);
        if (currentRequest !== requestId.current) return;
        if (!response?.status || !response.data) throw new Error(response?.message || 'Projects could not be loaded.');
        setData(response.data);
      } catch (requestError) {
        if (currentRequest === requestId.current) setError(requestErrorMessage(requestError));
      } finally {
        if (currentRequest === requestId.current) {
          setLoading(false);
          setPaginationLoading(false);
        }
      }
    }, query.search ? 300 : 0);
    return () => { clearTimeout(timer); requestId.current += 1; };
  }, [query]);

  const update = (key, value) => setQuery(current => ({ ...current, [key]: value, page: key === 'page' ? value : 1 }));
  const changePage = page => {
    setPaginationLoading(true);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    update('page', page);
  };
  const reset = () => setQuery({ search: '', customer_id: '', status: '', learning_hub: '', include_deleted: false, page: 1, page_size: 25 });
  const openProject = row => {
    const route = routeForTarget(row?.drilldown?.target || row?.links?.project?.target || { page: 'project', project_id: row.project_id });
    if (route) history.push(route);
  };
  const openCustomer = (event, row) => {
    event.stopPropagation();
    const route = routeForTarget(row?.links?.customer?.target || { page: 'customer', customer_id: row?.customer?.customer_id });
    if (route) history.push(route);
  };

  const filters = data?.filters || {};
  const columns = [
    { key: 'project', label: 'Project', render: row => <span style={{ fontWeight: 700 }}>{row.project || '-'}</span> },
    { key: 'customer', label: 'Customer', render: row => <Button size="small" color="primary" onClick={event => openCustomer(event, row)}>{row.customer?.name || '-'}</Button> },
    { key: 'status', label: 'Status', render: row => <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}><Badge tone={row.status === 'Complete' ? 'green' : 'blue'}>{row.status || '-'}</Badge>{row.overdue ? <Badge tone="amber">Overdue</Badge> : null}{row.is_deleted ? <Badge tone="red">Deleted</Badge> : null}</span> },
    { key: 'dates', label: 'Start – end', render: row => `${formatDate(row.start_date)} – ${formatDate(row.end_date)}` },
    { key: 'learners', label: 'Learners', align: 'right', render: row => <span>{formatCount(row.learners)}{row.test_learners ? <Typography component="span" variant="caption" color="textSecondary" style={{ display: 'block' }}>+ {formatCount(row.test_learners)} test</Typography> : null}</span> },
    { key: 'seats', label: 'Seats assigned / allocated', align: 'right', render: row => `${formatCount(row.seats_assigned)} / ${formatCount(row.seats_allocated)}` },
    { key: 'utilisation_pct', label: 'Utilisation', render: row => <UtilisationValue value={row.utilisation_pct} /> },
    { key: 'learning_hub', label: 'Learning Hub', render: row => <span style={{ display: 'grid', gap: 4 }}><LearningHubBadge value={row.learning_hub?.access_state} />{hubTiming(row.learning_hub) ? <Typography variant="caption" color="textSecondary">{hubTiming(row.learning_hub)}</Typography> : null}</span> },
    { key: 'learning_hub_enrollments', label: 'Enrolments', align: 'right', render: row => <span>{formatCount(row.learning_hub_enrollments)}{row.learning_hub_test_enrollments ? <Typography component="span" variant="caption" color="textSecondary" style={{ display: 'block' }}>+ {formatCount(row.learning_hub_test_enrollments)} test</Typography> : null}</span> },
  ];

  if (initialLoading || paginationLoading) return <>
    <LoadingProgress label="Loading projects" />
    <RouteSkeleton variant="table" showProgress={false} />
  </>;

  const SearchSurface = embedded ? 'div' : Panel;
  return (
    <SearchSurface style={embedded ? undefined : { marginTop: 18 }}>
      {loading ? <LoadingProgress label="Loading projects" /> : null}
      <div className={searchClasses.filterBar}>
        <TextField className={searchClasses.searchField} variant="outlined" size="small" label="Search projects" placeholder="Name or customer" value={query.search} onChange={event => update('search', event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <div className={searchClasses.filterGroup} data-testid="project-filter-group">
          <TextField className={searchClasses.filterControl} select variant="outlined" size="small" label="Customer" value={query.customer_id} onChange={event => update('customer_id', event.target.value)}><MenuItem value="">All customers</MenuItem>{customers.map(customer => <MenuItem key={customer.uuid || customer.customer_id || customer.id} value={customer.uuid || customer.customer_id || customer.id}>{customer.name || customer.company_name}</MenuItem>)}</TextField>
          <TextField className={searchClasses.filterControl} select variant="outlined" size="small" label="Status" value={query.status} onChange={event => update('status', event.target.value)}><MenuItem value="">All statuses</MenuItem>{(filters.status || [{ value: 'Open', label: 'Open' }, { value: 'Complete', label: 'Complete' }]).map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</TextField>
          <TextField className={searchClasses.filterControl} select variant="outlined" size="small" label="Learning Hub" value={query.learning_hub} onChange={event => update('learning_hub', event.target.value)}><MenuItem value="">All states</MenuItem>{(filters.learning_hub || []).map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</TextField>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', margin: '10px 0 14px' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}><FormControlLabel control={<Checkbox color="primary" checked={query.include_deleted} onChange={event => update('include_deleted', event.target.checked)} />} label="Include deleted" /><Button size="small" onClick={reset}>Clear filters</Button>{showFullSearchLink ? <Button size="small" color="primary" endIcon={<OpenInNewIcon fontSize="small" />} onClick={() => history.push(Paths.SuperadminProjects)}>Open full search</Button> : null}</div>
        <Typography variant="caption" color="textSecondary">{data ? `${formatCount(data.total)} projects` : ''}</Typography>
      </div>
      {error ? <div role="alert" style={{ marginBottom: 12, color: '#b91c1c' }}>{error} <Button size="small" onClick={() => setQuery(current => ({ ...current }))}>Try again</Button></div> : null}
      {!error && !loading && data && !data.results?.length ? <EmptyState>No projects match these filters.</EmptyState> : <DataTable ariaLabel="Project search results" columns={columns} rows={data?.results || []} onRowClick={openProject} getRowKey={row => row.project_id} />}
      {data?.results?.length ? <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
        <TextField select size="small" variant="outlined" label="Rows" value={query.page_size} onChange={event => update('page_size', Number(event.target.value))} style={{ width: 100 }}>{PAGE_SIZES.map(size => <MenuItem key={size} value={size}>{size}</MenuItem>)}</TextField>
        <Pagination
          page={query.page}
          onChange={(event, page) => changePage(page)}
          className={commonClasses.pagination}
          count={Math.max(1, Number(data.total_pages) || 1)}
          variant="outlined"
          shape="rounded"
          siblingCount={2}
          boundaryCount={2}
          disabled={loading}
        />
      </div> : null}
    </SearchSurface>
  );
};

export default ProjectSearch;
